"use client";

import Link from 'next/link';
import Image from 'next/image';
import { format } from 'date-fns';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, User, Calendar, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PostMetadata } from '@/lib/blog/directory';

interface BlogCardListItemProps {
  post: PostMetadata;
}

export function BlogCardListItem({ post }: BlogCardListItemProps) {
  return (
    <article 
      className="relative group focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-background rounded-lg"
      aria-labelledby={`post-title-${post.slug}`}
    >
      <Link 
        href={`/blog/${post.slug}`}
        className="absolute inset-0 z-10"
        aria-labelledby={`post-title-${post.slug}`}
        tabIndex={-1} // Hidden link for better a11y, main text is focusable below
      >
        <span className="sr-only">Read article: {post.title}</span>
      </Link>
      
      <Card className="overflow-hidden shadow-md group-hover:shadow-xl transition-all duration-300 h-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {post.image && (
            <div className="blog-card-image">
              <Image
                src={post.image}
                alt={post.title || `Featured image for blog post: ${post.slug}`}
                fill
                className="object-cover object-center"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 400px"
              />
            </div>
          )}
          
          <div className={cn(
            "p-6", 
            post.image ? 'md:col-span-2' : 'md:col-span-3'
          )}>
            <h3 
              id={`post-title-${post.slug}`}
              className="text-2xl font-bold mb-2 text-foreground group-hover:text-primary transition-colors flex items-center"
            >
              {post.title}
              <ArrowUpRight className="ml-2 h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" 
                aria-hidden="true" 
              />
            </h3>
            
            <div 
              className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-4"
              aria-label="Post metadata"
            >
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-primary mr-1.5" aria-hidden="true" />
                <time dateTime={new Date(post.date).toISOString()}>
                  {format(new Date(post.date), 'MMMM d, yyyy')}
                </time>
              </div>
              
              {post.author && (
                <div className="flex items-center">
                  <User className="h-4 w-4 text-primary mr-1.5" aria-hidden="true" />
                  <span>{post.author}</span>
                </div>
              )}
              
              {post.readingTime && (
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-primary mr-1.5" aria-hidden="true" />
                  <span>{post.readingTime} min read</span>
                </div>
              )}
            </div>
            
            <p className="text-muted-foreground mb-5 line-clamp-3">
              {post.description || post.summary || `Read more about ${post.title}`}
            </p>
            
            <div 
              className="flex flex-wrap gap-2 mt-auto"
              aria-label="Post tags"
            >
              {post.tags?.map((tag) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </article>
  );
}
